import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, effect, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';

import { Fai, Plan, PromotionsService as PromotionsServiceSale, QuoteModel, Sim } from '@bytel/bytel-sales';
import { CART_TYPES, IProductToAddInQuote } from '@bytel/checkout';

import { DataCyDirective } from '@base/common/directives/data-cy.directive';

import { AppService } from '@services/app.services';
import { ConfigurationService } from '@services/configuration.service';
import { MainCartService } from '@services/main-cart.service';
import { BYOU_CATEGORY, MIXED_CART_CATEGORY, MixedCartService } from '@services/mixed-cart.service';
import { PortabilityService } from '@services/portability.service';
import { PromotionsService } from '@services/promotions.service';
import { SimService } from '@services/sim.service';

import { SimContextModel } from '@models/sim-context.model';

import { ISim, SIM_TYPES } from '@interfaces/sim.interace';

import { BehaviorSubject, EMPTY, Observable, mergeMap } from 'rxjs';
import { debounceTime, map, tap } from 'rxjs/operators';

import { ParentalControlComponent } from '../../parental-control/parental-control.component';
import { PortabilityComponent } from '../../portability/portability.component';
import { SimComponent } from '../../sim/sim.component';
import { MultipleComponent } from './multiple/multiple.component';

@Component({
    selector: 'checkout-mobile-block',
    templateUrl: './mobile-block.component.html',
    styleUrl: 'mobile-block.component.scss',
    standalone: true,
    imports: [
        MultipleComponent,
        DataCyDirective,
        PortabilityComponent,
        ParentalControlComponent,
        AsyncPipe,
        SimComponent,
    ],
})
export class MobileBlockComponent implements OnInit {
    @Input() public cartForm: FormGroup;
    @Input() public isSubmitted: boolean;
    @Input() public isMobile: boolean;
    @Output() public simChoice = new EventEmitter<ISim>();

    public quoteIndex: number = 1;
    public mixedForm: FormGroup;
    public isLoading: boolean = false;

    public currentPlan: Plan;
    public plans: Plan[] = [];

    public sims: ISim[];
    public hasPhone: boolean = false;
    public currentSimChoice: ISim;
    public simContext: SimContextModel;
    public simContexts: SimContextModel[];
    public planList$: Observable<Plan[]>;
    private _planListSubject: BehaviorSubject<Plan[]> = new BehaviorSubject<Plan[]>([]);

    private appService: AppService = inject(AppService);
    private mixedCartService: MixedCartService = inject(MixedCartService);
    private cartService: MainCartService = inject(MainCartService);
    private formBuilder: FormBuilder = inject(FormBuilder);
    private portabilityService: PortabilityService = inject(PortabilityService);
    private promoService: PromotionsService = inject(PromotionsService);
    private simService: SimService = inject(SimService);
    private configService: ConfigurationService = inject(ConfigurationService);

    public constructor() {
        this.planList$ = this._planListSubject.asObservable();

        this.portabilityService.portaChoice$
            .pipe(
                debounceTime(200),
                map((data) => (data[this.quoteIndex] ? data[this.quoteIndex] : false)),
                mergeMap(() => this.initProducts()),
            )
            .subscribe();

        effect(() => {
            this.simContexts = this.simService.simContexts();
            this._getSimContext();
        });
    }

    public ngOnInit(): void {
        if (!this.cartService.getQuote(this.quoteIndex)) {
            this.cartService.addQuote();
        }
        const faiQuote = this.cartService.getQuoteFai();
        const mobilePlansToFetch = faiQuote?.getPrincipalProduct<Fai>('Fai')?.categories?.includes(BYOU_CATEGORY)
            ? BYOU_CATEGORY
            : MIXED_CART_CATEGORY;

        this.mixedForm = this.formBuilder.group({});
        this.cartForm.addControl('mixed-cart', this.mixedForm);

        this.mixedCartService
            .getMixedCartOffers(mobilePlansToFetch)
            .pipe(
                tap((plans) => {
                    this.plans = plans?.map((p) => {
                        p.data.max_connection = p.data.max_connection === '3g' ? '4g' : p.data.max_connection;
                        return p;
                    });
                }),
                mergeMap(() => this.initProducts()),
                tap(() => {
                    const quote = this.cartService.getQuote(this.quoteIndex);
                    const currentPlan = quote.getProductByType<Plan>(Plan);
                    if (currentPlan) {
                        this.changeOffer({ plan: currentPlan });
                    }
                }),
            )
            .subscribe();
    }

    public changeOffer(event: { plan: Plan; shouldReset?: boolean }): void {
        const { plan, shouldReset } = event;
        if (plan || shouldReset) {
            this.cartForm?.get('mixed-cart')?.get('mixed-choice')?.get('choiceControl')?.setErrors(null);
        } else if (this.currentPlan) {
            // sans le if currentPlan, au clic sur "non" le form serait tout le temps invalide
            setTimeout(() => {
                // à cause du bloc portability qui, en se démontant,
                // passe le form en statut valid et annule l'effet de la ligne ci-dessous
                // si elle n'est pas dans un setTimeout sans délais (pb de synchro)
                this.cartForm
                    ?.get('mixed-cart')
                    ?.get('mixed-choice')
                    ?.get('choiceControl')
                    ?.setErrors({ invalid: true });
            });
        }

        this.currentPlan = plan;
    }

    public onSimChoice = (simChoice: ISim): void => {
        this.simChoice.emit(simChoice);
    };

    protected initProducts(): Observable<Plan[]> {
        if (!this.plans?.length) {
            return EMPTY;
        }
        return this.initPromotions().pipe(
            tap((plans) => {
                this._planListSubject.next([...plans]);
            }),
        );
    }

    protected initPromotions(): Observable<Plan[]> {
        const productsInQuote: IProductToAddInQuote = {};
        const quote = this.cartService.getQuote(this.quoteIndex) || new QuoteModel({});
        const isCartWeb = this.cartService.cartModel.type === CART_TYPES.WEB;
        const rio: string = quote.forms.portability?.codeRio
            ? quote.forms.portability?.codeRio
            : isCartWeb
              ? 'fake'
              : null;
        this.plans.forEach((product) => (productsInQuote[product.gencode] = this.quoteIndex));
        return this.promoService
            .getPromotionForProducts(this.cartService.cartModel, productsInQuote, this.quoteIndex, rio)
            .pipe(
                tap((promotions) => {
                    this.plans.forEach((offer) =>
                        offer.calculatePrices(quote, PromotionsServiceSale.GetPromotionsForProduct(offer, promotions)),
                    );
                }),
                map(() => this.plans),
            );
    }

    protected _getSimContext(): void {
        if (!this.simContexts || !this.simContexts.map((sc) => sc.sims).flat().length) {
            this.appService.formStatusSubject.next({
                ...this.appService.formStatusSubject.getValue(),
                invalid: true,
            });
        }
        this.simContext = this.simContexts[this.quoteIndex];
        this.sims = this.simContexts.map((sc) => sc.sims).flat();
        const currentCartSim = this.cartService.getQuote(this.quoteIndex).getProductByType(Sim);
        const currentPlan = this.cartService.getQuote(this.quoteIndex).getProductByType<Plan>(Plan);
        const isSummerEdition = currentPlan.gencode === this.configService.getData().global.specialOffers.summerEdition;
        const eSimChoice = this.sims.find((sim) => sim.type === SIM_TYPES.QRESIM);
        const simChoice = this.sims.find((sim) => sim.isPrefered);
        if (currentCartSim) {
            this.currentSimChoice = this.sims.find((sim) => sim.gencode === currentCartSim.gencode);
            if (isSummerEdition && this.currentSimChoice?.type !== SIM_TYPES.QRESIM) {
                this.currentSimChoice = eSimChoice;
            }
        } else {
            this.currentSimChoice = isSummerEdition ? eSimChoice : simChoice;
        }
    }
}
